import React from 'react';
import styled from 'styled-components';
import { Link, GatsbyLinkProps } from 'gatsby';
import { theme } from 'styles/styled';
import FilledButton, { Skew } from 'components/Button/FilledButton';
import Text from 'components/Text';
import Heading from 'components/Heading/Heading';

type EducationTileVariant = 'normal' | 'registered-only';

export const EducationTile = styled.button`
  outline: none;
  position: relative;
  place-items: center;
  display: flex;
  max-width: 460px;
  width: 100%;
  flex-direction: column;
  padding: 4px ${theme.dimensions.spacingBase16 * 1.5}px
    ${theme.dimensions.spacingBase16 * 1.5}px
    ${theme.dimensions.spacingBase16 * 1.5}px;
  box-shadow: 0 4px 36px 0 #d4eeff, 0 2px 8px 0 rgba(118, 201, 255, 0.16);
  background: ${theme.palette.light};
  cursor: pointer;

  @media ${theme.devices.medium} {
    min-height: 410px;
    padding: ${theme.dimensions.spacingBase16 * 0.75}px 42px
      ${theme.dimensions.spacingBase16 * 1.5}px 42px;
    cursor: auto;
    box-shadow: ${theme.ui.boxShadowPaper};
  }
`;

export const CollapsedEducationTile = styled(EducationTile)`
  padding: 0;
  box-shadow: ${theme.ui.boxShadowPaper};
`;

export const EducationTileImage = styled.img`
  height: 140px;
  width: 185px;
  margin: 0 auto ${theme.dimensions.spacingBase16 * 0.75}px auto;
  object-fit: contain;

  @media ${theme.devices.medium} {
    height: 195px;
    width: 260px;
  }
`;

const EducationTileTitleStyles = styled(Heading)`
  font-size: ${theme.fontSize.title};
  letter-spacing: 0.2px;
  text-align: center;
  margin-bottom: ${theme.dimensions.spacingBase16 * 0.75}px;
  opacity: ${({ variant = 'normal' }: { variant?: EducationTileVariant }) =>
    variant === 'normal' ? '1' : '0.4'};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media ${theme.devices.medium} {
    font-size: ${theme.fontSize.pageTitleMobile};
  }
`;
export const EducationTileTitle = (
  props: React.PropsWithoutRef<JSX.IntrinsicElements['h3']> & {
    variant?: EducationTileVariant;
    isRtl: boolean;
  },
) => (
  <EducationTileTitleStyles
    as="h3"
    size="pageTitleMobile"
    color="dark"
    weight="medium"
    letterSpacing="tight"
    {...props}
  />
);

export const CollapsedEducationTileTitle = styled(EducationTileTitle)`
  margin-top: 18px;
  margin-bottom: 18px;
`;

export const EducationTileDescription = styled(Text)`
  font-size: ${theme.fontSize.copy};
  line-height: ${theme.dimensions.spacingBase16 * 1.25}px;
  letter-spacing: 0.1px;
  text-align: center;
  margin-bottom: 18px;

  color: ${({ variant = 'normal' }: { variant?: EducationTileVariant }) =>
    variant === 'normal' ? theme.palette.dark : '#bfbfbf'};

  -webkit-box-orient: vertical;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;

  @media ${theme.devices.medium} {
    -webkit-line-clamp: 3;
  }
`;

const EducationTileButtonStyles = styled(FilledButton)`
  position: absolute;
  bottom: -${theme.dimensions.spacingBase16 * 1.5}px;

  margin: auto auto 0 auto;
  font-size: ${theme.fontSize.copy};
  min-width: 150px;
  max-width: 350px;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  :hover {
    opacity: 1 !important;
    background-color: ${theme.palette.primaryLight};
  }

  @media ${theme.devices.medium} {
    position: static;
  }
`;
export const EducationTileButton = (props: {
  to: GatsbyLinkProps<{}>['to'];
  children: React.ReactNode;
  target?: JSX.IntrinsicElements['a']['target'];
  isRtl: boolean;
  rel?: 'noopener noreferrer';
}) => (
  <EducationTileButtonStyles skew={Skew['top-right']} as={Link} {...props} />
);

export const RegisteredUsersText = styled(Text)<{ marginTop?: string }>`
  margin-top: ${({
    marginTop = `${theme.dimensions.spacingBase16 * 2.25}px`,
  }) => marginTop};
  margin-bottom: 10px;
  color: #bfbfbf;
  font-size: ${theme.fontSize.copy};
  font-weight: ${theme.fontWeight.light};
  line-height: ${theme.dimensions.spacingBase16 * 1.5}px;
  letter-spacing: 0.15px;
  text-align: center;

  @media ${theme.devices.medium} {
    margin-top: 10px;
    font-size: ${theme.fontSize.label};
  }
`;

export const EducationWrapper = styled.section`
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: auto;
  grid-auto-flow: row;
  grid-gap: 45px;
  justify-items: center;
  margin: ${theme.dimensions.spacingBase16 * 1.25}px
    ${theme.dimensions.spacingBase16 * 1.25}px
    ${theme.dimensions.spacingBase16 * 1.5}px
    ${theme.dimensions.spacingBase16 * 1.25}px;

  @media ${theme.devices.medium} {
    grid-template-columns: auto;
    grid-template-rows: minmax(410px, auto);
    grid-gap: ${theme.dimensions.spacingBase16 * 1.25}px;
    margin: ${theme.dimensions.spacingBase16 * 1.25}px 0 110px 0;
  }

  @media ${theme.devices.large} {
    grid-template-columns: 460px 460px;
    grid-template-rows: repeat(2, minmax(410px, auto));
  }
`;

export const EducationHeading = styled(Heading)`
  width: 100%;
  font-size: 50px;
  margin-bottom: 4px;
  font-size: 26px;
  letter-spacing: 0.22px;
  padding-left: ${theme.dimensions.spacingBase16 * 1.25}px;
  margin-top: 24px;
  text-transform: capitalize;

  @media ${theme.devices.large} {
    letter-spacing: 0.78px;
    margin-bottom: 10px;
    font-size: 50px;
    padding-left: 0;
  }
`;
